<template>
  <div :class="wrapperClass" class="column">
    <b-form-group
      :id="filterLabelId"
      :label="labelIf"
      :label-for="filterId"
      label-align="left"
      :class="labelClass"
    >
      <template #default="label">
        <h2 v-if="styledLabel" class="styled-label">{{ styledLabel }}</h2>
        <div v-if="hasHovertip">
          <img
            v-if="hasHovertip"
            :id="getHovertipId"
            :src="hovertipSrc"
            alt="help"
            class="hovertip-help"
          />
          <b-popover
            v-if="hasHovertip"
            :target="getHovertipId"
            triggers="hover focus"
          >
            <template slot="title">
              {{ getHovertipTitle }}
            </template>
            {{ getHovertipText }}
          </b-popover>
        </div>
      </template>
    </b-form-group>
    <div class="filter-wrapper">
      <div class="inner-row">
        <b-form-input
          :id="filterId"
          :class="inputClass + ' form-control mr-sm-5'"
          :value="bindValue"
          @input="updateFilter($event)"
          type="text"
          :state="state"
          :required="elementRequired"
          :placeholder="filterPlaceholder"
          aria-describedby="input-live-help input-live-feedback"
        />
        <filter-clear-button
          v-if="bindValue?.length > 0"
          @click="updateFilter('')"
        />
      </div>
      <b-form-text :id="filterId + '-help'">
        {{ helpText }}
      </b-form-text>
    </div>
  </div>
</template>

<script>
import jsonApiFilterManager from "../../lib/JsonApiFilterManager";
import PathMapper from "../../lib/PathMapper";
import alert from "../../images/alert.svg";
import FilterClearButton from "./FilterClearButton.vue";
import helpOutline from "../raft/help-Outline.svg";

export default {
  name: "ShortTextFilter",
  components: { FilterClearButton },
  data() {
    return {
      filterText: "",
      hovertipSrc: helpOutline,
      selected: "",
    };
  },
  props: {
    id: { type: String, default: "" },
    label: { type: String, default: "" },
    placeholder: { type: String, default: "" },
    updateMethod: { type: Function, default: null },
    getValue: { type: String, default: "" },
    storeWatch: { type: String, default: "" },
    storeCommit: { type: String, default: "" },
    path: { type: String, default: "" },
    operator: { type: String, default: "CONTAINS" },
    isRequired: Boolean,
    state: { type: Boolean, default: null },
    hovertip: { type: Object, default: null },
    helpText: { type: String, default: "" },
    wrapperClass: { type: String, default: "" },
    inputClass: { type: String, required: false, default: "" },
    useJsonApiFilterManager: { type: Boolean, default: true },
    debounce: { type: Number, default: 0 },
    filterAction: { type: String, required: false },
    filterKey: { type: String, required: false },
    styledLabel: { type: String, default: null },
  },
  methods: {
    updateFilter(value) {
      this.$emit("update-filter", {
        id: this.id,
        path: this.path,
        operator: this.operator,
        useJsonApiFilterManager: this.useJsonApiFilterManager,
        value,
        filterAction: this.filterAction,
        filterKey: this.filterKey,
      });
      if (Boolean(this.storeCommit) && this.storeCommit.length > 1) {
        return this.$store.commit(this.storeCommit, value);
      }
      if (this.updateMethod) {
        return this.updateMethod(value, this.path);
      }
    },
  },
  computed: {
    bindValue: function () {
      if (this.$store.getters[this.storeWatch]) {
        return this.$store.getters[this.storeWatch];
      }
      return this.getValue;
    },
    filterId: function () {
      return this.id;
    },
    filterLabelId: function () {
      return this.id + "-label";
    },
    labelIf: function () {
      if (this.label.length < 1) {
        return "";
      }
      return this.label;
    },
    filterPlaceholder: function () {
      if (this.placeholder && this.placeholder.length > 0) {
        return this.placeholder;
      }
      return "";
    },
    elementRequired: function () {
      return this.isRequired === true;
    },
    labelClass: function () {
      if (this.isRequired) {
        return "form-required";
      }
      return "";
    },
    getHelpIconPath: function () {
      return PathMapper.getIconAssetPath("help.png", this.$isB4);
    },
    hasHovertip: function () {
      return this.hovertip;
    },
    getHovertipId: function () {
      return this.hovertip.id;
    },
    getHovertipTitle: function () {
      return this.hovertip.title;
    },
    getHovertipText: function () {
      return this.hovertip.text;
    },
  },
  mounted() {
    if (this.useJsonApiFilterManager && !this.filterAction) {
      this.$store.watch(
        (state, getters) => this.$store.getters[this.storeWatch],
        (newValue) => {
          if (newValue.length < 1 && newValue.length > 0) {
            return;
          }
          if (!newValue) {
            jsonApiFilterManager.deleteFiltersByName([this.id]);
            return;
          }
          jsonApiFilterManager.addFilter(
            this.id,
            this.path,
            newValue,
            this.operator,
            this.storeCommit,
            newValue,
          );
        },
      );
    }
  },
};
</script>

<style scoped>
:is(#raft-js, #raft-js-app) :is(.form-group, .filters) >>> label {
  margin-bottom: 0;
  display: block;
}

/* Needed so the red shows when the input is focused in Chrome */
.filter-wrapper >>> input {
  outline-offset: 4px;
  margin: 0.125rem 0.125rem 0.125rem 0;
  width: calc(12.5rem * 0.9);
}

.filter-wrapper >>> .form-control.is-invalid {
  border-color: #dc3545;
  padding-right: calc(1.5em + 0.75rem) !important;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%23dc3545'%3E%3Ccircle cx='6' cy='6' r='4.5'/%3E%3Cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3E%3Ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right calc(0.375em + 0.1875rem) center;
  background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
}

.column {
  display: flex;
  flex-direction: column;
}

.inner-row {
  display: flex;
  flex-direction: row;
}

.hovertip-help {
  height: 0.75rem;
}

.styled-label {
  font-size: var(--font-size-75);
  margin: 0;
  padding: 0;
}
</style>
